/**
 * Which codec a recording is written in, decided per capture rather than per browser.
 *
 * `MediaRecorder.isTypeSupported` answers for the codec and knows nothing about the picture. It
 * says yes to H.264 on every Chromium, and the encoder then refuses the first frame of a picture
 * larger than H.264 level 5.2 allows: 36 864 macroblocks, 9.4 megapixels — 4096×2304, or a native
 * 4K screen. A HiDPI monitor in a scaled mode is past that: a 4K panel shown as 2304×1296 points
 * is captured at 4608×2592, 46 656 macroblocks. Chromium has no fallback to another codec at that
 * point; the recorder fires `error` and `stop` on its first frame, and the recording ends the
 * moment it begins with nothing in it.
 *
 * The limit is OpenH264's, Chromium's software H.264 encoder, and it binds everywhere: the
 * hardware encoders are tried first and stop even earlier (4096×2304 on macOS), and a hardware
 * failure falls back to software, never to another codec. VP9 and VP8 have no limit a screen
 * could reach.
 */

/** A video frame's dimensions in pixels. */
export interface FrameSize {
	width: number;
	height: number;
}

/**
 * The most macroblocks an H.264 encoder Chromium ships will take in one frame — level 5.2's
 * `MaxFS`, and OpenH264's `MAX_MBS_PER_FRAME`. A macroblock is 16×16 pixels.
 */
export const H264_MAX_MACROBLOCKS = 36_864;

/** One codec the recorder may write, with the size limit its encoders impose where there is one. */
interface RecordingCodec {
	mimeType: string;
	/** Whether a frame of this size can be encoded at all; absent where size never decides. */
	fits?: (frame: FrameSize) => boolean;
}

/**
 * Macroblocks in a frame, counted the way the encoder counts them: a partial block at the edge
 * is a whole block.
 *
 * @param frame - The frame's dimensions.
 * @returns The number of 16×16 blocks covering it.
 */
export function macroblockCount({ width, height }: FrameSize) {
	return Math.ceil(width / 16) * Math.ceil(height / 16);
}

/**
 * H.264-in-WebM is Mux's fast path, so it leads wherever the picture allows it; VP9 and VP8 are
 * the fallbacks every Chromium answers, and plain WebM lets the browser choose. The first type
 * the frame fits and `isTypeSupported` accepts wins.
 */
const CODECS: RecordingCodec[] = [
	{
		mimeType: "video/webm;codecs=h264",
		fits: (frame) => macroblockCount(frame) <= H264_MAX_MACROBLOCKS,
	},
	{ mimeType: "video/webm;codecs=vp9" },
	{ mimeType: "video/webm;codecs=vp8" },
	{ mimeType: "video/webm" },
];

/**
 * The size of the picture a set of tracks records, read off the tracks' own settings.
 *
 * The largest video track decides, because a composed recording is the size of its screen: the
 * camera-bubble compositor draws the camera into the screen's frame, and the composed track it
 * hands out may report no size of its own until frames flow. Audio tracks and tracks that report
 * no dimensions are skipped.
 *
 * @param tracks - The recorded stream's tracks and the capture tracks behind them.
 * @returns The largest frame among them, or null when no track reports a size.
 */
export function captureFrameSize(tracks: MediaStreamTrack[]): FrameSize | null {
	let largest: FrameSize | null = null;
	for (const track of tracks) {
		if (track.kind !== "video") continue;
		const { width, height } = track.getSettings();
		if (!width || !height) continue;
		if (!largest || width * height > largest.width * largest.height) {
			largest = { width, height };
		}
	}
	return largest;
}

/**
 * Choose the container and codec for a recording of this size.
 *
 * A frame whose size is unknown is not offered H.264. The wrong answer there costs the whole
 * recording — it dies on its first frame — while the right one costs Mux's fast path, so unknown
 * is treated as too large.
 *
 * @param frame - The picture's dimensions, or null when no track reported them.
 * @param isTypeSupported - The browser's own answer for a type; injectable for the tests.
 * @returns The MIME type to hand `MediaRecorder`, or null when the browser supports none of them
 * and is left to its own default.
 */
export function pickRecordingMimeType(
	frame: FrameSize | null,
	isTypeSupported: (type: string) => boolean = (type) => MediaRecorder.isTypeSupported(type),
): string | null {
	for (const codec of CODECS) {
		if (codec.fits && (!frame || !codec.fits(frame))) continue;
		if (isTypeSupported(codec.mimeType)) return codec.mimeType;
	}
	return null;
}
